import React, { FunctionComponent, useEffect, useState } from 'react';
import { IntlProvider as ReactIntlProvider } from 'react-intl';

import { useAuthentication } from '../context/Authentication';

import messages, { SUPPORTED_LANGUAGES } from '../i18n';

export type Language = (typeof SUPPORTED_LANGUAGES)[number];
type LanguageSource = 'organization' | 'browser';

export const LANGUAGE_SOURCE_PRIORITY: readonly LanguageSource[] = ['browser', 'organization'];
export const DEFAULT_FALLBACK_LANGUAGE: Language = 'en';

function isSupportedLanguage(language: string | undefined): language is Language {
  return language !== undefined && SUPPORTED_LANGUAGES.includes(language as Language);
}

function toSupportedLanguage(language: string | undefined): Language | undefined {
  if (!language) return undefined;
  const normalized = language.trim().toLowerCase();
  const [baseLanguage] = normalized.split(/[-_]/);
  return isSupportedLanguage(baseLanguage) ? baseLanguage : undefined;
}

function getBrowserLanguage(): Language | undefined {
  const browserLanguages = [...(navigator.languages || []), navigator.language];
  for (const candidate of browserLanguages) {
    const language = toSupportedLanguage(candidate);
    if (language) return language;
  }
  return undefined;
}

export const getLanguage = (organizationLanguage: string | undefined): Language => {
  const normalizedOrganizationLanguage = toSupportedLanguage(organizationLanguage);
  const browserLanguage = getBrowserLanguage();

  for (const source of LANGUAGE_SOURCE_PRIORITY) {
    if (source === 'organization' && normalizedOrganizationLanguage) {
      return normalizedOrganizationLanguage;
    }
    if (source === 'browser' && browserLanguage) {
      return browserLanguage;
    }
  }

  return DEFAULT_FALLBACK_LANGUAGE;
};

type Props = {
  children?: React.ReactNode;
};

export const IntlProvider: FunctionComponent<Props> = ({ children }) => {
  const { organizationLanguage } = useAuthentication();
  const [language, setLanguage] = useState(getLanguage(organizationLanguage));

  useEffect(() => {
    const onLanguageChange = (): void => {
      setLanguage(getLanguage(organizationLanguage));
    };
    window.addEventListener('languagechange', onLanguageChange);
    return () => {
      window.removeEventListener('languagechange', onLanguageChange);
    };
  }, [organizationLanguage]);

  return (
    <ReactIntlProvider locale={language} messages={messages[language]}>
      {children}
    </ReactIntlProvider>
  );
};
